import {
  Box,
  Card,
  Chevron,
  Popover,
  Tag,
  TagColor,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type Leave from 'modules/timeoff/store/models/Leave'

interface Props {
  status: StatusType
  updatedAt: Date
  leave: Leave
  confirmedStartDate?: Date | null
  confirmedEndDate?: Date | null
  errorMessages: errorMessage[] | null
}

const Statuses = {
  NOT_REQUESTED: 'not_requested',
  CONFIRMED: 'confirmed',
  FAILED_REQUEST: 'failed_request',
  UNKNOWN: 'unknown',
  PENDING: 'requested',
  READ_ERROR: 'read_error',
  INCORRECT_INSURANCE_COMPANY: 'incorrect_insurance_company',
  CERTIFICATE_NOT_PRESENT: 'certificate_not_present',
  DATES_NO_MATCH: 'dates_no_match',
}

interface errorMessage {
  error: string
}
type StatusType = (typeof Statuses)[keyof typeof Statuses]

interface StatusData {
  type: TagColor
  tagTranslation: string
  description: string
}

const EauResponse = ({
  status,
  updatedAt,
  confirmedStartDate,
  confirmedEndDate,
  errorMessages,
  leave,
}: Props) => {
  const [open, toggle] = useToggle()

  const statusData = (status: StatusType) => {
    const data: StatusData = {
      type: TagColor.neutral,
      tagTranslation: i18n.t('time_off.eau_confirmation.responses.statuses.unknown'),
      description: '',
    }
    if (
      status === Statuses.CONFIRMED &&
      ((confirmedStartDate &&
        confirmedStartDate.toISOString().split('T')[0] !== leave.attributes.start_on) ||
        (confirmedEndDate &&
          confirmedEndDate.toISOString().split('T')[0] !== leave.attributes.finish_on))
    ) {
      status = Statuses.DATES_NO_MATCH
    }

    if (status === Statuses.NOT_REQUESTED) {
      data['type'] = TagColor.radical
      data['tagTranslation'] = i18n.t('time_off.eau_confirmation.responses.statuses.discarded')
      data['description'] = i18n.t('time_off.eau_confirmation.responses.descriptions.discarded')
    } else if (status === Statuses.DATES_NO_MATCH) {
      data['type'] = TagColor.tangerine
      data['tagTranslation'] = i18n.t('time_off.eau_confirmation.responses.statuses.dates_no_match')
      data['description'] = i18n.t(
        'time_off.eau_confirmation.responses.descriptions.dates_no_match',
        {
          start_date:
            confirmedStartDate?.toLocaleString('en-US', { month: 'long', day: 'numeric' }) ?? '-',
          end_date:
            confirmedEndDate?.toLocaleString('en-US', { month: 'long', day: 'numeric' }) ?? '-',
          year: confirmedEndDate?.getFullYear() ?? '-',
        }
      )
    } else if (status === Statuses.CONFIRMED) {
      data['type'] = TagColor.lime
      data['tagTranslation'] = i18n.t('time_off.eau_confirmation.responses.statuses.confirmed')
      data['description'] = i18n.t('time_off.eau_confirmation.responses.descriptions.confirmed', {
        start_date:
          confirmedStartDate?.toLocaleString('en-US', { month: 'long', day: 'numeric' }) ?? '-',
        end_date:
          confirmedEndDate?.toLocaleString('en-US', { month: 'long', day: 'numeric' }) ?? '-',
        year: confirmedEndDate?.getFullYear() ?? '-',
      })
    } else if (status === Statuses.CERTIFICATE_NOT_PRESENT) {
      data['type'] = TagColor.radical
      data['tagTranslation'] = i18n.t('time_off.eau_confirmation.responses.statuses.not_found')
      data['description'] = i18n.t('time_off.eau_confirmation.responses.descriptions.not_found')
    } else if (status === Statuses.FAILED_REQUEST) {
      data['type'] = TagColor.radical
      data['tagTranslation'] = i18n.t('time_off.eau_confirmation.responses.statuses.failed')
      data['description'] = i18n.t('time_off.eau_confirmation.responses.descriptions.failed')
    } else if (status === Statuses.INCORRECT_INSURANCE_COMPANY) {
      data['type'] = TagColor.radical
      data['tagTranslation'] = i18n.t('time_off.eau_confirmation.responses.statuses.failed')
      data['description'] = i18n.t(
        'time_off.eau_confirmation.responses.descriptions.incorrect_insurance_company'
      )
    } else if (status === Statuses.PENDING) {
      data['type'] = TagColor.champagne
      data['tagTranslation'] = i18n.t('time_off.eau_confirmation.responses.statuses.pending')
      data['description'] = i18n.t('time_off.eau_confirmation.responses.descriptions.pending')
    } else if (status === Statuses.READ_ERROR) {
      data['type'] = TagColor.radical
      data['tagTranslation'] = i18n.t('time_off.eau_confirmation.responses.statuses.failed')
      data['description'] = i18n.t('time_off.eau_confirmation.responses.descriptions.read_error')
    }
    return data
  }

  const padZero = (num: number) => (num < 10 ? `0${num}` : `${num}`)

  const updatedAtString = (updatedAt: Date) => {
    if (isNaN(updatedAt.getTime())) {
      return ''
    }
    return `${i18n.t('time_off.eau_confirmation.form.updated')}: ${padZero(
      updatedAt.getHours()
    )}:${padZero(updatedAt.getMinutes())},
   ${dateToString(updatedAt, 'ui_abbreviated_month_year')}`
  }

  const data = statusData(status)

  return (
    <Card>
      <Card.Eyebrow>
        <Tag type={data['type']}>{data['tagTranslation']}</Tag>
      </Card.Eyebrow>
      <Card.Title size="small">{i18n.t('time_off.eau_confirmation.form.title')}</Card.Title>
      <Card.Content>
        <Box gap="s8">
          <Text.Normal color="grey800">{data['description']}</Text.Normal>
          {!!errorMessages?.length && (
            <Popover.ItemList
              attachment="top center"
              targetAttachment="bottom center"
              toggle={toggle}
              open={open}
              title={i18n.t('common.more_information')}
            >
              <Box flexDirection="row" alignItems="center">
                <Text.Normal size="200" color="radicalRed1200">
                  {i18n.t('common.more_information')}
                </Text.Normal>
                <Chevron direction={open ? 'up' : 'down'} type="radical" />
              </Box>
              {errorMessages.map((message, index) => (
                <Text.Normal key={index} size="200" color="grey000White">
                  {message.error}
                </Text.Normal>
              ))}
            </Popover.ItemList>
          )}
        </Box>
        <Box paddingTop="s24">
          <Text.Normal color="grey800">{updatedAtString(updatedAt)}</Text.Normal>
        </Box>
      </Card.Content>
    </Card>
  )
}

export default EauResponse
